import { useState, useEffect } from 'react';
import { initializeConfig } from './config';

// Loading component displayed while configuration is being loaded
const LoadingScreen = () => (
  <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
    <div className="text-center">
      <div className="size-8 mx-auto mb-4 text-primary">
        <svg
          fill="none"
          viewBox="0 0 48 48"
          xmlns="http://www.w3.org/2000/svg"
          className="animate-spin"
        >
          <path
            d="M24 4v8m0 24v8m16-20h-8M8 24H0m35.3-11.3l-5.7 5.7M18.4 29.6l-5.7 5.7M35.3 35.3l-5.7-5.7M18.4 18.4l-5.7-5.7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Loading Configuration
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Please wait while we load the application settings...
      </p>
    </div>
  </div>
);

// Error component displayed if configuration loading fails
const ErrorScreen = ({ error, onRetry }) => (
  <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
    <div className="text-center max-w-md mx-auto px-4">
      <div className="size-12 mx-auto mb-4 text-red-500">
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Configuration Error
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        Failed to load application configuration. The app will use default settings.
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
        Error: {error}
      </p>
      <button
        onClick={onRetry}
        className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
      >
        <svg
          className="w-4 h-4 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Retry
      </button>
    </div>
  </div>
);

// Configuration provider component that loads config before rendering children
const ConfigProvider = ({ children }) => {
  const [configState, setConfigState] = useState({
    loading: true,
    error: null,
    loaded: false
  });

  const loadConfiguration = async () => {
    setConfigState({ loading: true, error: null, loaded: false });

    try {
      await initializeConfig();
      setConfigState({ loading: false, error: null, loaded: true });
    } catch (error) {
      console.error('Failed to initialize configuration:', error);
      setConfigState({
        loading: false,
        error: error.message || 'Unknown configuration error',
        loaded: false
      });
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const handleRetry = () => {
    loadConfiguration();
  };

  // Show loading screen while configuration is being loaded
  if (configState.loading) {
    return <LoadingScreen />;
  }

  // Show error screen if configuration failed to load
  if (configState.error) {
    return <ErrorScreen error={configState.error} onRetry={handleRetry} />;
  }

  // Configuration loaded successfully, render the app
  return children;
};

export default ConfigProvider;
